import { IntegrityMonitorRuleSetFormView } from '../view/IntegrityMonitorRuleSetFormView';
import { templateIntegrityMonitorRuleSetForm } from '../view/template';
import { IntegrityMonitorRuleSetFormRepository } from '../repository/IntegrityMonitorRuleSetFormRepository';
import { BaseWebComponentController } from '../../../../framework/BaseWebComponentController';
import { IntegrityMonitorRuleSetFormWebComponent } from '../index';
import { IntegrityMonitorRuleSetFormWebComponentSchema, RuleSetFormEvents } from '../types';
import { showErrorToast, showSuccessToast } from '@nirvana/shared/common';

export class IntegrityMonitorRuleSetFormController extends BaseWebComponentController {
  public component: IntegrityMonitorRuleSetFormWebComponent;
  public dataRepository: IntegrityMonitorRuleSetFormRepository;
  public view: IntegrityMonitorRuleSetFormView;
  public viewConstructor: typeof IntegrityMonitorRuleSetFormView;
  public dataRepositoryConstructor: typeof IntegrityMonitorRuleSetFormRepository;

  constructor(component: IntegrityMonitorRuleSetFormWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: IntegrityMonitorRuleSetFormView,
      viewTemplate: templateIntegrityMonitorRuleSetForm,
      dataRepositoryConstructor: IntegrityMonitorRuleSetFormRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$saveButton,
        event: 'click',
        callback: this.saveRuleSet,
      },
      {
        element: this.view.dom.$cancelButton,
        event: 'click',
        callback: this.closeRuleSetForm,
      },
    ];
  }

  async getDataForView(): Promise<IntegrityMonitorRuleSetFormWebComponentSchema> {
    const data = {} as IntegrityMonitorRuleSetFormWebComponentSchema;

    data.uniqueRuleSetNameValidator = this.dependencies.api.clients.IntegrityMonitorRuleSets.checkNameIsUnique;

    if (this.component.ruleSetId) {
      data.ruleSetData = await this.dataRepository.getRuleSetData(this.component.ruleSetId);
    }

    return data;
  }

  private async saveRuleSet() {
    if (!this.view.formGroup.valid) {
      this.view.formGroup.validate();
      return;
    }

    this.view.dom.$saveButton.showSpinner = true;
    this.view.dom.$saveButton.disabled = true;

    const record: Object = this.view.formGroup.value;

    record['companyId'] = this.dependencies.getActiveCompany().companyId;

    try {
      if (this.component.ruleSetId) {
        await this.saveEditedRuleSet(record);
      } else {
        await this.saveNewRuleSet(record);
      }
    } catch (error) {
      // if the exception is AuthenticationSecurityException it is already treated by the general session exception
      if (error?.exceptionType === 'Exceptions\\AuthenticationSecurityException') {
        return;
      }

      showErrorToast(error);
    } finally {
      // remove the spinner
      this.view.dom.$saveButton.showSpinner = false;
      this.view.dom.$saveButton.disabled = false;
    }
  }

  private async saveEditedRuleSet(record: Object) {
    record['_id'] = this.component.ruleSetId;
    await this.dataRepository.updateRuleSet(record);
    showSuccessToast(this.dependencies.translateFn('Rule set edited successfully.'));
    this.closeRuleSetForm();
    this.component.dispatchEvent(
      new CustomEvent(RuleSetFormEvents.RULE_SET_EDITED, {
        detail: {
          ruleSetId: record['_id'],
          ruleSetName: record['name'],
        },
      })
    );
  }

  private async saveNewRuleSet(record: Object) {
    const rules = this.component.rules ?? [];
    record['rulesIds'] = rules.map(rule => rule._id);
    await this.dataRepository.createRuleSet(record);
    showSuccessToast(this.dependencies.translateFn('Rule set created successfully.'));
    this.closeRuleSetForm();
    this.component.dispatchEvent(new CustomEvent(RuleSetFormEvents.RULE_SET_CREATED));
  }

  private closeRuleSetForm() {
    // Empty the form, so that the following Form Open will display SAVE as disabled while loading the data
    this.view.setFormData(undefined);
    this.view.unsubscribeObservers();
    this.component.isVisible = false;
    this.component.rules = undefined;
    this.component.ruleSetId = undefined;
  }
}
